import { t } from "./i18n";
import { useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { authUrl } from "./api";
import {
  navigationTarget,
  rememberPendingReturnTo,
  safeReturnTo,
} from "./route-auth";
import { startAuthentication } from "@simplewebauthn/browser";
import { Sparkles } from "lucide-react";

export function Login() {
  const location = useLocation();
  const currentTarget = navigationTarget(location);
  const requestedReturnTo = new URLSearchParams(location.search).get(
    "returnTo",
  );
  const returnTo = requestedReturnTo
    ? safeReturnTo(requestedReturnTo, currentTarget)
    : currentTarget;
  const [slug, setSlug] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [methods, setMethods] = useState<
    Array<{
      id: string;
      kind: "oidc" | "native";
      name: string;
      nativePolicy: {
        passwordEnabled: boolean;
        passkeyEnabled: boolean;
        magicLinkEnabled: boolean;
      };
    }>
  >([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    rememberPendingReturnTo(returnTo);
  }, [returnTo]);
  const discover = async () => {
    if (!slug) return [];
    const response = await fetch(
      authUrl(`organizations/${encodeURIComponent(slug)}/methods`),
      { credentials: "include" },
    );
    const next = response.ok ? await response.json() : [];
    setMethods(next);
    return next as typeof methods;
  };
  const passwordLogin = async () => {
    setBusy(true);
    setError("");
    try {
      const response = await fetch(
        authUrl(`native/${encodeURIComponent(slug)}/login`),
        {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ email, password, mfaCode }),
        },
      );
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error ?? "Sign-in failed");
      window.location.assign(returnTo);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Sign-in failed");
    } finally {
      setBusy(false);
    }
  };
  const passkeyLogin = async () => {
    setBusy(true);
    setError("");
    try {
      const optionsResponse = await fetch(
        authUrl(`passkey/${encodeURIComponent(slug)}/options`),
        {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ email }),
        },
      );
      const options = await optionsResponse.json();
      if (!optionsResponse.ok)
        throw new Error(options.error ?? "Passkey is unavailable");
      const response = await startAuthentication({
        optionsJSON: options.options,
      });
      const verification = await fetch(authUrl("passkey/verify"), {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ challengeId: options.challengeId, response }),
      });
      const result = await verification.json().catch(() => ({}));
      if (!verification.ok)
        throw new Error(result.error ?? "Passkey sign-in failed");
      window.location.assign(returnTo);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Passkey sign-in failed",
      );
    } finally {
      setBusy(false);
    }
  };
  const native = methods.find((method) => method.kind === "native");
  return (
    <main className="grid min-h-screen place-items-center overflow-hidden bg-[#edf9f5] p-6 dark:bg-[#0d1714]">
      <div className="absolute top-[-30%] left-1/2 size-[700px] -translate-x-1/2 rounded-full bg-lumen-300/30 blur-3xl" />
      <div className="panel relative w-full max-w-md p-9 text-center">
        <div className="mx-auto mb-6 grid size-14 place-items-center rounded-2xl bg-lumen-600 text-white shadow-lg shadow-lumen-600/20">
          <Sparkles />
        </div>
        <h1 className="text-3xl font-bold tracking-tight">
          {t("Think brighter with Lumen")}
        </h1>
        <p className="mx-auto mt-3 max-w-sm text-sm leading-6 text-stone-500">
          {t(
            "One thoughtful workspace for your organization’s language and image models.",
          )}
        </p>
        <div className="mt-7 space-y-3 text-left">
          <input
            className="field w-full"
            placeholder={t("Organization slug")}
            value={slug}
            onChange={(event) => {
              setSlug(event.target.value.toLowerCase());
              setMethods([]);
            }}
            onBlur={() => void discover()}
          />
          <button
            type="button"
            className="button w-full"
            disabled={!slug || busy}
            onClick={() => void discover()}
          >
            {t("Find sign-in methods")}
          </button>
          {methods.length > 0 && (
            <>
              <input
                className="field w-full"
                type="email"
                placeholder={t("Email")}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
              {native?.nativePolicy.passwordEnabled && (
                <>
                  <input
                    className="field w-full"
                    type="password"
                    placeholder={t("Password")}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                  <input
                    className="field w-full"
                    inputMode="numeric"
                    placeholder={t("MFA or recovery code (if enabled)")}
                    value={mfaCode}
                    onChange={(event) => setMfaCode(event.target.value)}
                  />
                  <button
                    className="button-primary w-full"
                    disabled={busy || !email || !password}
                    onClick={() => void passwordLogin()}
                  >
                    {t("Sign in with password")}
                  </button>
                </>
              )}
              {native?.nativePolicy.passkeyEnabled && (
                <button
                  className="button w-full"
                  disabled={busy || !email}
                  onClick={() => void passkeyLogin()}
                >
                  {t("Sign in with a passkey")}
                </button>
              )}
              {methods
                .filter((method) => method.kind === "oidc")
                .map((method) => (
                  <a
                    key={method.id}
                    className="button w-full"
                    href={authUrl(
                      `oidc/${encodeURIComponent(slug)}/${method.id}`,
                    )}
                    onClick={() => rememberPendingReturnTo(returnTo)}
                  >
                    {method.name}
                  </a>
                ))}
            </>
          )}
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
        <a
          href={authUrl("login")}
          onClick={() => rememberPendingReturnTo(returnTo)}
          className="button mt-4 w-full py-3"
        >
          {t("Use default organization sign-in")}
        </a>
        <a
          href={authUrl("dev")}
          onClick={() => rememberPendingReturnTo(returnTo)}
          className="mt-3 block text-xs text-stone-400 hover:text-lumen-600"
        >
          {t("Development sign-in")}
        </a>
      </div>
    </main>
  );
}
